import type {
  CreateEmergencyRiderSessionInput,
  ReassignRiderSessionInput,
  ReleaseRiderSessionInput,
  RetimeRiderSessionInput,
  ReviewRiderGuaranteeInput,
  Tables,
  VoidRiderSessionInput,
} from "@bednbody/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";
import { isServiceDate } from "./iso-week";

/**
 * Rider Session supply, monitoring and outcomes.
 *
 * Reads are plain `.select()`s guarded by 0062/0063's RLS — there is
 * deliberately no read endpoint (see `rider-sessions/index.ts`'s header), and
 * adding one here would be a second implementation of the visibility rule. The
 * only Edge calls are the two writes: generation and the guarantee review.
 */

/** Generated batches, newest service date first. Hub-scoped by RLS, not here. */
export function useRiderSessionBatches(hubId: string | null) {
  return useQuery({
    queryKey: ["rider-session-batches", hubId],
    queryFn: async () => {
      let query = supabase
        .from("rider_session_batches")
        .select(
          // `origin` (0196) so a manual batch never reads as a generated one.
          "id, hub_id, service_date, required_total, session_a, session_b, session_c, final_therapist_count, generated_at, origin, hubs ( name )",
        )
        .order("service_date", { ascending: false })
        .limit(60);
      if (hubId) query = query.eq("hub_id", hubId);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
  });
}

export type RiderSessionBatchRow = NonNullable<
  ReturnType<typeof useRiderSessionBatches>["data"]
>[number];

/**
 * Every Session for one service date — the live monitoring read.
 *
 * 15 s polling, matching `DispatchPage`'s queue reads. Not Realtime: a Session
 * changes state a handful of times a night, and the states that matter most
 * (`active` at the scheduled start, the sweep's outcomes) are written by a
 * pg_cron tick rather than by a user action anyone is watching for.
 */
export function useRiderSessionsForDate(serviceDate: string, hubId: string | null) {
  return useQuery({
    queryKey: ["rider-sessions-day", serviceDate, hubId],
    // A cleared date input sends "", which Postgres rejects as a date — and the
    // panel then blamed the connection. No date, no request.
    enabled: isServiceDate(serviceDate),
    queryFn: async () => {
      let query = supabase
        .from("rider_sessions")
        .select(
          // `voided_at` and the batch's `origin` (0196) are read so the row can
          // say which kind of supply it is. A manually created Session that
          // looked identical to a generated one is exactly what the quarantine
          // exists to prevent — hiding it in the UI would undo that at the last
          // step.
          "id, code, hub_id, service_date, starts_at, ends_at, state, outcome, access, started_at, ended_at, actual_extension_minutes, max_extension_hours, secured_rider_id, voided_at, staff!rider_sessions_secured_rider_id_fkey ( full_name, rider_rank ), hubs ( name ), rider_session_batches ( origin )",
        )
        .eq("service_date", serviceDate)
        .order("starts_at");
      if (hubId) query = query.eq("hub_id", hubId);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

export type RiderSessionRow = NonNullable<
  ReturnType<typeof useRiderSessionsForDate>["data"]
>[number];

type OutcomeEarnings = Pick<
  Tables<"rider_session_earnings">,
  | "rider_session_id"
  | "total_php"
  | "base_php"
  | "peak_incentive_php"
  | "extension_fee_php"
  | "guarantee_php"
  | "total_km"
  | "dispatch_count"
>;

/**
 * Finished Sessions with their settled earnings — the outcomes surface.
 *
 * The money shown here is the SPEC model (base fare + per-km + peak +
 * extension + guarantee — packages/logic/rider-earnings.ts). Since 0118
 * (OQ-71 switchover, 2026-08-23) this is also what `run_payroll` pays — the
 * two no longer disagree.
 */
export function useRiderSessionOutcomes(hubId: string | null, days: number, withEarnings: boolean) {
  return useQuery({
    queryKey: ["rider-session-outcomes", hubId, days, withEarnings],
    queryFn: async () => {
      const since = new Date(Date.now() - days * 86_400_000);
      const sinceDate = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila" }).format(since);
      let query = supabase
        .from("rider_sessions")
        .select(
          "id, code, hub_id, service_date, starts_at, ends_at, started_at, ended_at, state, outcome, actual_extension_minutes, staff!rider_sessions_secured_rider_id_fkey ( full_name, rider_rank ), hubs ( name )",
        )
        .not("outcome", "is", null)
        .gte("service_date", sinceDate)
        .order("service_date", { ascending: false })
        .limit(200);
      if (hubId) query = query.eq("hub_id", hubId);
      const { data, error } = await query;
      if (error) throw error;
      const sessions = data ?? [];

      // ⚠ EARNINGS ARE A SECOND READ, AND ONLY FOR ROLES THAT MAY SEE PAY.
      // hub_manager has no SELECT on `rider_session_earnings` (0064 withholds pay
      // from that role), and PostgREST fails the WHOLE query on an embed the
      // caller cannot read — so while earnings were embedded, the Outcomes tab
      // never loaded for a hub manager at all.
      const earnings = new Map<string, OutcomeEarnings>();
      if (withEarnings && sessions.length > 0) {
        const { data: rows, error: earningsError } = await supabase
          .from("rider_session_earnings")
          .select(
            "rider_session_id, total_php, base_php, peak_incentive_php, extension_fee_php, guarantee_php, total_km, dispatch_count",
          )
          .in(
            "rider_session_id",
            sessions.map((row) => row.id),
          );
        if (earningsError) throw earningsError;
        for (const row of rows ?? []) earnings.set(row.rider_session_id, row);
      }

      return sessions.map((row) => ({
        ...row,
        rider_session_earnings: earnings.get(row.id) ?? null,
      }));
    },
    staleTime: 30_000,
  });
}

export type RiderSessionOutcomeRow = NonNullable<
  ReturnType<typeof useRiderSessionOutcomes>["data"]
>[number];

/**
 * Sessions an emergency closed that are waiting on a guarantee decision
 * (RIDER-028), read from `v_rider_session_guarantee_queue` (0067). The view is
 * `security_invoker`, so it cannot widen anyone's scope — a reader sees exactly
 * what their own RLS on `rider_sessions` already allows.
 */
export function useGuaranteeReviewQueue(enabled: boolean) {
  return useQuery({
    queryKey: ["rider-guarantee-queue"],
    // hub_manager has no grant on the view (0067: "this is pay"), so the read
    // is never made for that role rather than made and failed.
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_rider_session_guarantee_queue")
        .select("*")
        .order("flagged_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
    refetchInterval: 60_000,
  });
}

export type GuaranteeQueueRow = NonNullable<
  ReturnType<typeof useGuaranteeReviewQueue>["data"]
>[number];

/** Decisions already taken — the record, kept visible beside the queue. */
export function useGuaranteeReviewHistory(enabled: boolean) {
  return useQuery({
    queryKey: ["rider-guarantee-reviews"],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rider_session_guarantee_reviews")
        .select(
          // The Session's code and date, so two decisions for one rider can be told apart.
          "rider_session_id, decision, amount_php, reason, decided_at, staff!rider_session_guarantee_reviews_rider_id_fkey ( full_name ), rider_sessions ( code, service_date )",
        )
        .order("decided_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
  });
}

/**
 * §4.1–§4.6 generation — a date and an area, and nothing that decides supply.
 *
 * ⚠ THE OPERATOR NO LONGER SUPPLIES THE COUNT. OQ-66 and OQ-70 both recorded
 * that `cycleId`, `countLockId`, `templateVersion` and `finalTherapistCount`
 * were request fields only until CORE-05A's Friday 7:00 PM lock existed, and
 * that the endpoint would read the lock and delete them when it did. It exists
 * (`therapist_final_counts`, 0076), so the server resolves all four and a
 * `FINAL_COUNT_NOT_LOCKED` refusal now means finalization has not run for that
 * week — never that someone should type the number instead.
 */
export function useGenerateRiderSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { hubId: string; serviceDate: string }) =>
      callEdgeFunction<{
        id: string;
        requiredTotal: number;
        sessionA: number;
        sessionB: number;
        sessionC: number;
        alreadyExisted: boolean;
      }>("rider-sessions/generate", {
        data: { hubId: input.hubId, serviceDate: input.serviceDate },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rider-session-batches"] });
      queryClient.invalidateQueries({ queryKey: ["rider-sessions-day"] });
    },
  });
}

/**
 * RIDER-028 — record a guarantee decision. Superadmin-only at the Edge route
 * because the authority is undefined (never-invent-policy.md §2), and audited
 * inside `review_rider_session_guarantee`.
 *
 * ⚠ Recording is all it does. It does not change the settled earnings row and
 * it does not reach `run_payroll` — see 0067's header.
 */
export function useReviewRiderGuarantee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sessionId: string } & ReviewRiderGuaranteeInput) => {
      const { sessionId, ...body } = input;
      return callEdgeFunction(`admin/rider-sessions/${sessionId}/guarantee-review`, { data: body });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rider-guarantee-queue"] });
      queryClient.invalidateQueries({ queryKey: ["rider-guarantee-reviews"] });
    },
  });
}

// ── manual Session overrides — OQ-122 ───────────────────────────────────────
//
// ⚠ CORE-05B §2 and §7.4 say in writing that Admin must not manage Session
// supply by hand. The client overruled them (DECISIONS.md OQ-122, relayed by
// Leon 2026-09-04); 0193 carries the full argument and every use is audited.

/**
 * Riders who could take a Session in this hub — the picker's options.
 *
 * Hub-scoped HERE and not only by RLS, unlike every read above. That is not a
 * second implementation of a policy: 0193 REFUSES a cross-hub rider outright
 * (`RIDER_WRONG_AREA`, CORE-05B §4.7), so offering one would be offering a
 * choice the server has already decided to reject. `active` is filtered for the
 * same reason — `RIDER_NOT_ELIGIBLE`.
 */
export function useAssignableRiders(hubId: string | null) {
  return useQuery({
    queryKey: ["assignable-riders", hubId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff")
        .select("id, full_name, rider_rank")
        .eq("role", "rider")
        .eq("active", true)
        .eq("hub_id", hubId!)
        .order("full_name");
      if (error) throw error;
      return data;
    },
    enabled: hubId !== null,
    staleTime: 60_000,
  });
}

export type AssignableRider = NonNullable<ReturnType<typeof useAssignableRiders>["data"]>[number];

/** Everything a Session override touches, refetched together. */
function invalidateRiderSessions(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["rider-sessions-day"] });
  void queryClient.invalidateQueries({ queryKey: ["assignable-riders"] });
}

/**
 * Hand a not-yet-started Session to another Rider — or assign an unclaimed one
 * directly, bypassing CORE-05B §4.8's first-come-first-served.
 *
 * ⚠ `maxExtensionHours` IS ASKED FOR, NOT CARRIED OVER. REND-005 puts the choice
 * before the secure action, and the incoming Rider is a different person making
 * it. A UI that reused the outgoing Rider's number would be committing someone
 * to an allowance they never picked.
 */
export function useReassignRiderSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sessionId: string } & ReassignRiderSessionInput) => {
      const { sessionId, ...body } = input;
      return callEdgeFunction(`admin/rider-sessions/${sessionId}/reassign`, { data: body });
    },
    onSuccess: () => invalidateRiderSessions(queryClient),
  });
}

/** Return a not-yet-started Session to the pool without naming a replacement. */
export function useReleaseRiderSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sessionId: string } & ReleaseRiderSessionInput) => {
      const { sessionId, ...body } = input;
      return callEdgeFunction(`admin/rider-sessions/${sessionId}/release`, { data: body });
    },
    onSuccess: () => invalidateRiderSessions(queryClient),
  });
}

/**
 * Move a not-yet-started Secured Session's starts_at/ends_at within its own
 * service_date (0245) — Superadmin only, narrower than the reassign/release
 * pair above (which OQ-122 grants to Admin too). See
 * `useRetimeTherapistSession`'s comment for the full reasoning; it's the same
 * decision, mirrored to the rider side.
 */
export function useRetimeRiderSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sessionId: string } & RetimeRiderSessionInput) => {
      const { sessionId, ...body } = input;
      return callEdgeFunction(`admin/rider-sessions/${sessionId}/retime`, { data: body });
    },
    onSuccess: () => invalidateRiderSessions(queryClient),
  });
}

/**
 * Create a Session that CORE-05B's Saturday engine never produced, and assign it.
 *
 * ⚠ THIS IS THE ONE THE SPEC FORBIDS BY NAME — §2: "Admin or Hub Manager must
 * not manually create extra supply outside the approved generation rules." It
 * exists on the client's instruction (OQ-122). Everything it makes is
 * quarantined in an `emergency_override` batch so it can never be counted as
 * generated supply; `run_payroll` reads what these Sessions earn, so that
 * distinction has to survive.
 */
export function useCreateEmergencyRiderSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEmergencyRiderSessionInput) =>
      callEdgeFunction("admin/rider-sessions", { data: input }),
    onSuccess: () => {
      invalidateRiderSessions(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["rider-session-batches"] });
    },
  });
}

/**
 * Void a manually created Session.
 *
 * Generated Sessions are NOT voidable — §4.10 forbids removing one after its
 * batch commits, and the server answers `NOT_EMERGENCY_SUPPLY`. Un-assigning a
 * generated Session is `useReleaseRiderSession`, which is a different act.
 */
export function useVoidRiderSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sessionId: string } & VoidRiderSessionInput) => {
      const { sessionId, ...body } = input;
      return callEdgeFunction(`admin/rider-sessions/${sessionId}/void`, { data: body });
    },
    onSuccess: () => invalidateRiderSessions(queryClient),
  });
}
